"use client";

import { useQuery } from "@tanstack/react-query";

import { type LastTradesTableEntry } from "#/components/LastTradesTable/LastTradesTable.types";
import { ONE_SECOND_MS } from "#/config/time";
import { useRealtimeLastTradesFeatureFlag } from "#/hooks/featureFlags/useRealtimeLastTradesFeatureFlag";

import { fetchLastTradesByInstrument } from "../kyan.service";
import {
  type LastTrade,
  type LastTradesByInstrumentRequest,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const transformLastTradesByInstrument = (
  trades: LastTrade[],
): LastTradesTableEntry[] => {
  const tradeHistory = trades
    .map((trade) => ({
      price: trade.average_price,
      size: trade.size,
      time: trade.timestamp,
      isBuy: trade.direction === "buy",
      isTaker: trade.order_id === trade.trade_id,
    }))
    .filter((t) => t.isTaker) // Best temporary solution to deduplicate trades.
    .sort((a, b) => b.time - a.time);
  return tradeHistory;
};

type LastTradesByInstrumentParams = {
  disabled?: boolean;
} & Partial<LastTradesByInstrumentRequest>;

export const useLastTradesByInstrument = (
  params: LastTradesByInstrumentParams,
) => {
  const { disabled, ...requestParams } = params;
  const { isRealtimeLastTradesEnabled } = useRealtimeLastTradesFeatureFlag();

  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["fetchLastTradesByInstrument", requestParams],
    queryFn: () =>
      defaultApiResponseHandler({
        serviceName: "Fetch Last Trades",
        payload: requestParams as LastTradesByInstrumentRequest,
        service: fetchLastTradesByInstrument,
      }),
    select: transformLastTradesByInstrument,
    enabled:
      !isRealtimeLastTradesEnabled &&
      Boolean(requestParams.instrument_name) &&
      !disabled,
    refetchInterval: 20 * ONE_SECOND_MS,
  });

  return {
    data,
    isLoading,
    refetch,
  };
};
